import { estimateReadingTime } from '@/lib/resource-reading-time.mjs'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import Sidebar from '@/components/learn/Sidebar'
import PostList from '@/components/learn/PostList'
import LearnBreadcrumbs from '@/components/learn/LearnBreadcrumbs'
import CategoryHeading from '@/components/learn/category-heading'
import MetaTags from '@/components/MetaTags'
import { client } from '@/sanity/lib/client'

dayjs.extend(relativeTime)

export default function Resources({
  posts,
  categories,
  categoriesWithPostCounts,
  currentPage,
  totalPages,
}) {
  return (
    <>
      <MetaTags
        pageTitle={
          currentPage > 1 ? `Resources - Page ${currentPage}` : 'Resources'
        }
        pageDescription="Resources on eCommerce, web design, and digital marketing."
        path={currentPage > 1 ? `/resources?page=${currentPage}` : '/resources'}
      />
      <div>
        <LearnBreadcrumbs currentPage={currentPage} />

        <div className="wrapper mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <CategoryHeading
              title="Resources"
              description="Stay up to date with the latest in eCommerce, web design, and digital marketing."
            />
            <PostList
              posts={posts}
              currentPage={currentPage}
              totalPages={totalPages}
              baseUrl="/resources"
            />
          </div>
          <div className="lg:col-span-4">
            <Sidebar
              categories={categories}
              categoriesWithPostCounts={categoriesWithPostCounts}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export async function getServerSideProps(context) {
  const { page = '1' } = context.query
  const currentPage = parseInt(page, 10)
  const postsPerPage = 15
  const offset = (currentPage - 1) * postsPerPage

  const [posts, postCount, categoriesWithPostCounts] = await Promise.all([
    client.fetch(
      `*[_type == "post"] | order(publishedAt desc)[$offset...$end] {
        _id,
        title,
        publishedAt,
        slug,
        mainImage,
        "categories": categories[]->{
          _id,
          title,
          slug
        },
        body[_type == "block"]{_type, children[]{text}},
        excerpt,
        "author": author->name
      }`,
      { offset, end: offset + postsPerPage },
    ),
    client.fetch(`count(*[_type == "post"])`),
    client.fetch(`
      *[_type == "category"] {
        _id,
        title,
        slug,
        "postCount": count(*[_type == "post" && references(^._id)])
      } | order(postCount desc)
    `),
  ])

  const totalPages = Math.ceil(postCount / postsPerPage)

  if (!posts) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      posts: posts.map(({ body, ...post }) => ({ ...post, readingTime: estimateReadingTime(body) })),
      categories: categoriesWithPostCounts,
      postCount,
      categoriesWithPostCounts,
      currentPage,
      totalPages,
    },
  }
}
